import { useState } from "react";
import { useDispatch } from "react-redux";
import { endTrialSubscription, getCurrentSubscription } from "./service";
import { updateCurrentSubscription } from "./subscriptionSlice";
import { mixpanelCustomEvent } from "components/mixpanel/eventTriggers";
import { MixpanelEventName } from "components/mixpanel/types";
import { Button } from "components/shadcn/ui/button";
import { CurrentSubscriptionDetails, TRIAL_USER_SOURCE } from "./types";
import UnlockFullQuotaNowModal from "./confirm-unlock-full-quota/confirmUnlockFullQuota";
import lang from "lang";
import Loader from "components/common/loader";

interface UnlockQuotaBannerProps {
  userData: any;
  currentSubscriptionDetails: CurrentSubscriptionDetails;
  source: string;
  onUnlockSuccess?: (success: boolean) => void;
  onLoadingStateChange?: (loading: boolean) => void;
}

const UnlockFullQuotaBanner: React.FC<UnlockQuotaBannerProps> = ({
  userData,
  currentSubscriptionDetails,
  source,
  onUnlockSuccess,
  onLoadingStateChange,
}) => {
  const { campaign: campaignCopy, domainSettings: domainSettingsCopy } = lang;
  const trialDays = process.env.REACT_APP_TRIAL_PERIOD_DAYS;

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const handleUnlockQuota = async () => {
    setLoading(true);
    onLoadingStateChange?.(true);
    try {
      setShowConfirmModal(false);
      const endTrialResponse = await endTrialSubscription(
        currentSubscriptionDetails?.id
      );
      if (endTrialResponse) {
        const currentSubscription = await getCurrentSubscription();
        if (currentSubscription) {
          dispatch(updateCurrentSubscription(currentSubscription));
          onUnlockSuccess?.(true);
        }
        mixpanelCustomEvent({
          mixpanelProps: {
            End_Trial: source,
          },
          id: userData?.uid.toString(),
          eventName: MixpanelEventName.clicksEndTrialNow,
        });
      }
    } catch (error) {
      console.error("Error ending trial:", error);
    } finally {
      setLoading(false);
      onLoadingStateChange?.(false);
    }
  };

  return (
    <>
      {loading && source !== TRIAL_USER_SOURCE.EXPRESS_INTEREST_MODAL && (
        <Loader />
      )}
      <div
        className={`w-full flex justify-center ${
          source === TRIAL_USER_SOURCE.SEARCH_PAGE
            ? ""
            : source === TRIAL_USER_SOURCE.CAMPAIGN
            ? "w-full flex items-center justify-between bg-[--primary-100] text-pink-900 rounded-t-[24px]"
            : source === TRIAL_USER_SOURCE.EXPRESS_INTEREST_MODAL ||
              source === TRIAL_USER_SOURCE.DOMAIN_SETTINGS_DIALOG
            ? "flex items-center"
            : ""
        }`}
      >
        <div
          className={`flex justify-between p-4 bg-[--primary-100] text-pink-900 rounded-[24px] ${
            source === TRIAL_USER_SOURCE.CAMPAIGN ||
            source === TRIAL_USER_SOURCE.EXPRESS_INTEREST_MODAL ||
            source === TRIAL_USER_SOURCE.DOMAIN_SETTINGS_DIALOG
              ? "w-full"
              : "w-[75%]"
          }`}
        >
          {source === TRIAL_USER_SOURCE.DOMAIN_SETTINGS_DIALOG && (
            <div className="flex flex-col justify-start">
              <p className="font-semibold text-md">
                {domainSettingsCopy.activateSubscriptionDomainSettingsHeading}
              </p>
              <p className="font-normal text-sm">
                {
                  domainSettingsCopy.activateSubscriptionDomainSettingsSubheading
                }
              </p>
            </div>
          )}
          {source !== TRIAL_USER_SOURCE.DOMAIN_SETTINGS_DIALOG && (
            <p className="text-sm font-medium text-gray-950 text-left">
              {source === TRIAL_USER_SOURCE.EXPRESS_INTEREST_MODAL
                ? campaignCopy.trialUserBannerTextForExpressInterestModal
                : campaignCopy.trialUserBannerText.replace(
                    "{{days}}",
                    trialDays ?? "0"
                  )}
            </p>
          )}
          <Button
            variant="primary"
            className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 text-sm font-medium rounded-[32px]"
            onClick={() => setShowConfirmModal(true)}
            disabled={loading}
          >
            <span className="text-white font-inter text-sm font-semibold leading-5 text-left underline-from-font">
              {source === TRIAL_USER_SOURCE.EXPRESS_INTEREST_MODAL ||
              source === TRIAL_USER_SOURCE.DOMAIN_SETTINGS_DIALOG
                ? campaignCopy.activateSubscription
                : campaignCopy.unlockFullQouta}
            </span>
          </Button>
        </div>

        {showConfirmModal && (
          <UnlockFullQuotaNowModal
            onClose={() => setShowConfirmModal(false)}
            handleUnlockQuota={handleUnlockQuota}
            isOpen={showConfirmModal}
            source={source}
          />
        )}
      </div>
    </>
  );
};

export default UnlockFullQuotaBanner;
